<!-- Component for displaying a single Out of Area Protection (OAP) item for a plan -->
<template>
	<div
		class="mt-2.5 mb-2 print:mt-2"
		v-if="mockProductOap"
		v-auto-animate
	>
		<!-- Parent item -->
		<div
			class="hover:bg-primary-50 my-3 flex flex-1 cursor-pointer items-center"
			@click="oapDropdown = !oapDropdown"
		>
			<p class="text-primary-700 text-sm font-bold">
				{{ t('oap.transferGeneric') }}
			</p>
			<ChevronDownIcon
				:class="oapDropdown ? 'rotate-180' : ''"
				class="text-primary-700 ml-1 size-4 cursor-pointer text-sm"
			/>
			<div class="ml-auto text-sm text-slate-500">
				{{ t('reviewSlide.included') }}
			</div>
		</div>

		<!-- Local -->
		<div
			:class="{ hidden: !oapDropdown }"
			class="print:block"
		>
			<div
				class="hover:bg-primary-50 my-3 ml-4 grid grid-cols-[minmax(0,1fr)_max-content] grid-rows-1 items-center overflow-hidden print:my-2"
			>
				<div class="flex flex-1 items-center">
					<p class="text-primary-700 text-sm">
						{{ t('oap.transferLocal') }}
					</p>
				</div>
			</div>

			<!-- OAP -->
			<SwipeableToggle
				target-class="hover:bg-primary-50"
				v-model="isOapIncluded"
				:class="{ 'print:hidden': !isOapIncluded }"
			>
				<div
					class="hover:bg-primary-50 ml-4 grid w-full grid-cols-[minmax(0,1fr)_max-content] grid-rows-1 items-center gap-4 overflow-hidden print:my-2"
				>
					<div class="flex flex-1 items-center">
						<p
							class="text-primary-700 text-sm"
							:class="{
								'text-secondary-400! line-through': !isOapIncluded,
							}"
						>
							{{ t('oap.oap') }}
						</p>
					</div>
					<div
						class="text-sm text-slate-500"
						:class="{
							'text-secondary-300! line-through': !isOapIncluded,
						}"
					>
						{{ formatPrice(mockProductOap.fixedPrice) }}
					</div>
				</div>
			</SwipeableToggle>
		</div>
	</div>
</template>

<script setup lang="ts">
import Plan from '@/classes/Plan'
import SwipeableToggle from '@/components/common/SwipeableToggle.vue'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import { ChevronDownIcon } from '@heroicons/vue/24/solid'
import { computed, ref, type PropType, watch } from 'vue'

const { formatPrice, mockProductOap } = useMerchandise()
const { t } = usePresentationLocale()
const oapDropdown = ref<boolean>(false)

const emit = defineEmits<{
	(e: 'update:oapIncluded', oapIncluded: boolean, plan: Plan): void
}>()

const props = defineProps({
	plan: {
		type: Object as PropType<Plan>,
		required: true,
	},
	parentDeleted: Boolean,
})

const isOapIncluded = computed({
	get() {
		return props.plan?.oapIncluded
	},
	set(newValue) {
		emit('update:oapIncluded', newValue, props.plan)
	},
})

// Watch for parentDeleted prop changes and update plan.oapIncluded accordingly
watch(
	() => props.parentDeleted,
	(isDeleted) => {
		if (isDeleted) {
			props.plan.oapIncluded = false
		} else {
			props.plan.oapIncluded = true
		}
	}
)
</script>
